// Consent-gated PostHog loader (DPDP-clean).
// - Nothing runs until the user grants "Product analytics" consent.
// - initAnalytics() is idempotent: safe to call on every consent change.
// - Revoking consent immediately opts out capturing and stops session recording.
//
// Consent lives in localStorage under `ap_cookie_consent_v1`:
//   { essential: true, diagnostics: bool, analytics: bool, at: ISO }

const CONSENT_KEY = "ap_cookie_consent_v1";
const POSTHOG_KEY = "phc_xAvL2Iq4tFmANRE7kzbKwaSqp1HJjN7x48s3vr0CMjs";
const POSTHOG_HOST = "https://us.i.posthog.com";

let loaderPromise = null;

function hasAnalyticsConsent() {
  try {
    const raw = localStorage.getItem(CONSENT_KEY);
    if (!raw) return false;
    return !!JSON.parse(raw)?.analytics;
  } catch {
    return false;
  }
}

// Inject the PostHog stub loader on demand. Never runs at import time.
function loadPosthogStub() {
  if (loaderPromise) return loaderPromise;
  loaderPromise = new Promise((resolve) => {
    if (typeof window === "undefined") return resolve(null);
    if (window.posthog && window.posthog.__SV) return resolve(window.posthog);

    // Minimal PostHog snippet (paraphrased from posthog-js docs).
    // The stub queues calls until array.js loads. array.js is only fetched
    // when init() is called below.
    /* eslint-disable */
    !(function (t, e) {
      var o, n, p, r;
      e.__SV ||
        ((window.posthog = e),
        (e._i = []),
        (e.init = function (i, s, a) {
          function g(t, e) {
            var o = e.split(".");
            2 == o.length && ((t = t[o[0]]), (e = o[1])),
              (t[e] = function () {
                t.push([e].concat(Array.prototype.slice.call(arguments, 0)));
              });
          }
          ((p = t.createElement("script")).type = "text/javascript"),
            (p.crossOrigin = "anonymous"),
            (p.async = !0),
            (p.src =
              s.api_host.replace(".i.posthog.com", "-assets.i.posthog.com") +
              "/static/array.js"),
            (r = t.getElementsByTagName("script")[0]).parentNode.insertBefore(
              p,
              r,
            );
          var u = e;
          for (
            void 0 !== a ? (u = e[a] = []) : (a = "posthog"),
              u.people = u.people || [],
              u.toString = function (t) {
                var e = "posthog";
                return (
                  "posthog" !== a && (e += "." + a),
                  t || (e += " (stub)"),
                  e
                );
              },
              u.people.toString = function () {
                return u.toString(1) + ".people (stub)";
              },
              o =
                "init me ws ys ps bs capture je Di ks register register_once register_for_session unregister unregister_for_session Ps getFeatureFlag getFeatureFlagPayload isFeatureEnabled reloadFeatureFlags updateEarlyAccessFeatureEnrollment getEarlyAccessFeatures on onFeatureFlags onSurveysLoaded onSessionId getSurveys getActiveMatchingSurveys renderSurvey canRenderSurvey canRenderSurveyAsync identify setPersonProperties group resetGroups setPersonPropertiesForFlags resetPersonPropertiesForFlags setGroupPropertiesForFlags resetGroupPropertiesForFlags reset get_distinct_id getGroups get_session_id get_session_replay_url alias set_config startSessionRecording stopSessionRecording sessionRecordingStarted captureException loadToolbar get_property getSessionProperty Es $s createPersonProfile Is opt_in_capturing opt_out_capturing has_opted_in_capturing has_opted_out_capturing clear_opt_in_out_capturing Ss debug xs getPageViewId captureTraceFeedback captureTraceMetric".split(
                  " ",
                ),
              n = 0;
            n < o.length;
            n++
          )
            g(u, o[n]);
          e._i.push([i, s, a]);
        }),
        (e.__SV = 1));
    })(document, window.posthog || []);
    /* eslint-enable */
    resolve(window.posthog);
  });
  return loaderPromise;
}

let initialised = false;

async function initPosthog() {
  const ph = await loadPosthogStub();
  if (!ph) return;
  if (!initialised) {
    ph.init(POSTHOG_KEY, {
      api_host: POSTHOG_HOST,
      person_profiles: "identified_only",
      // Belt + braces: refuse to capture anything until we explicitly opt in.
      opt_out_capturing_by_default: true,
      capture_pageview: false,
      disable_session_recording: true,
      autocapture: false,
    });
    initialised = true;
  }
  ph.opt_in_capturing();
  // Session recording + pageview only turn on after opt-in.
  ph.startSessionRecording?.({ recordCrossOriginIframes: false });
  ph.capture?.("$pageview");
}

function teardownPosthog() {
  const ph = typeof window !== "undefined" ? window.posthog : null;
  if (!ph || !initialised) return;
  ph.stopSessionRecording?.();
  ph.opt_out_capturing?.();
}

/** Idempotent: call on every consent change or app boot. */
export function syncAnalyticsWithConsent() {
  if (hasAnalyticsConsent()) {
    initPosthog();
  } else {
    teardownPosthog();
  }
}

/** Explicit init helper (currently just an alias). */
export const initAnalytics = syncAnalyticsWithConsent;
